import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import "./MarkdownPost.css";

import { markdown_posts } from "../../assets/tv/markdown_posts";

/**
 * CRA/Webpack-friendly local image loader for /assets/images
 * - expects filenames like "alien_001.jpg" to exist in src/assets/images/
 */
const images = require.context("../../assets/images", false, /\.(png|jpe?g|webp|gif)$/i);

function resolveLocalAsset(src?: string): string | null {
    if (!src) return null;
    if (!src.startsWith("asset:")) return null;

    const filename = src.replace("asset:", "").trim();
    if (!filename) return null;

    try {
        // CRA/webpack returns a string URL for the asset
        return images<string>(`./${filename}`);
    } catch (e) {
        console.error("Missing local image:", filename, e);
        return null;
    }
}


export default function MarkdownPost() {
    const { slug } = useParams();

    const post = useMemo(
        () => markdown_posts.find((p) => p.slug === slug),
        [slug]
    );

    if (!post) {
        return (
            <Container style={{ paddingTop: 30 }}>
                <h1>Post not found</h1>
                <p>Nothing here. (Yet!)</p>
            </Container>
        );
    }

    return (
        <Container style={{ paddingTop: 30, paddingBottom: 80 }}>
            <Row>
                <Col lg={8}>
                    <h1 style={{ marginBottom: 6 }}>{post.name}</h1>
                    {post.subtitle && <p style={{ marginTop: 0, color: "dimgrey" }}>{post.subtitle}</p>}
                    <p style={{ color: "dimgrey" }}>
                        {post.date} —{" "}
                        {post.reviewer.twitter ? (
                            <a href={post.reviewer.twitter} target="_blank" rel="noopener noreferrer">
                                {post.reviewer.name}
                            </a>
                        ) : (
                            post.reviewer.name
                        )}
                    </p>

                    {/* Optional hero */}
                    {post.hero?.image && (
                        <figure style={{ marginTop: 18 }}>
                            <img
                                style={{ width: "100%", borderRadius: 8 }}
                                src={resolveLocalAsset(`asset:${post.hero.image}`) ?? ""}
                                alt={post.hero.alt ?? ""}
                            />
                            {post.hero.text && (
                                <figcaption style={{ textAlign: "center", marginTop: 6 }}>
                                    <h4 style={{ fontSize: "1rem", margin: 0 }}>{post.hero.text}</h4>
                                </figcaption>
                            )}
                        </figure>
                    )}

                    <div className="mdPost" style={{ marginTop: 24 }}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            urlTransform={(url) => {
                                if (url.startsWith("asset:")) return url;   // ✅ allow your local scheme
                                return defaultUrlTransform(url);            // ✅ keep safe default behavior
                            }}
                            components={{
                                img: ({ src, alt, title }) => {
                                    console.log("IMG src raw:", src, "JSON:", JSON.stringify(src));

                                    const resolved = resolveLocalAsset(src);
                                    if (!resolved) {
                                        return <span style={{ color: "crimson" }}>Missing image: {String(src)}</span>;
                                    }

                                    return (
                                        <figure style={{ margin: "22px 0" }}>
                                            <img style={{ width: "100%", borderRadius: 8 }} src={resolved} alt={alt ?? ""} />
                                            {title && (
                                                <figcaption style={{ textAlign: "center", marginTop: 6 }}>
                                                    <h4 style={{ fontSize: "1rem", margin: 0 }}>{title}</h4>
                                                </figcaption>
                                            )}
                                        </figure>
                                    );
                                },
                                h2: ({ children }) => <h2 style={{ marginTop: 26 }}>{children}</h2>,
                                h3: ({ children }) => <h3 style={{ marginTop: 20 }}>{children}</h3>,
                                p: ({ children }) => <div style={{ fontSize: 18, lineHeight: 1.6, margin: "0 0 16px" }}>{children}</div>,
                                li: ({ children }) => <li style={{ fontSize: 18, lineHeight: 1.6 }}>{children}</li>,
                            }}
                        >
                            {post.content}
                        </ReactMarkdown>
                    </div>

                    {typeof post.rating === "number" && (
                        <p style={{ fontSize: 18, marginTop: 30 }}>
                            <strong>Rating:</strong> {post.rating} stars
                        </p>
                    )}
                </Col>
            </Row>
        </Container>
    );
}
